export type CellBucket = 'good' | 'in_flight' | 'retry' | 'problem';

export interface GridCell {
  readonly storeId: number;
  readonly platform: string;
  readonly date: string;
  readonly status: string;
  readonly bucket: CellBucket;
  readonly rowCount: number;
  readonly lastVerifiedAt: string | null;
  readonly severity: 'warning' | 'critical' | null;
}

export interface FleetGridResponse {
  readonly days: number;
  readonly cells: GridCell[];
}

export interface ActivityEvent {
  readonly id: string;
  readonly storeId: number;
  readonly platform: string;
  readonly runType: string;
  readonly status: string;
  readonly startedAt: string | null;
  readonly completedAt: string | null;
  readonly rowsIngested: number;
  readonly errorMessage: string | null;
  readonly triggerSource: string | null;
  readonly backfillGroupId: string | null;
}

export interface FleetActivityResponse {
  readonly days: number;
  readonly limit: number;
  readonly offset: number;
  readonly events: ActivityEvent[];
}

export interface PlatformProgress {
  readonly platform: string;
  readonly totalDays: number;
  readonly doneDays: number;
  readonly problemDays: number;
  readonly expectedDays: number;
  readonly pct: number;
}

export interface FleetSummaryResponse {
  readonly campaignStart: string;
  readonly overallPct: number;
  readonly totalDays: number;
  readonly doneDays: number;
  readonly problemDays: number;
  readonly expectedDays: number;
  readonly etaMs: number | null;
  readonly stores: {
    readonly total: number;
    readonly done: number;
    readonly withProblems: number;
    readonly inProgress: number;
  };
  readonly throughputRowsPerMin: number;
  readonly platforms: PlatformProgress[];
}

// Tailwind classes per bucket — mirrors SlaPage cell color conventions.
export const BUCKET_CLASSES: Record<CellBucket, string> = {
  good: 'bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-300',
  in_flight: 'bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300',
  retry: 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300',
  problem: 'bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300',
};

export const BUCKET_LABEL: Record<CellBucket, string> = {
  good: 'Verified',
  in_flight: 'In progress',
  retry: 'Needs retry',
  problem: 'Failed',
};

export function formatRows(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

export function timeAgo(iso: string | null): string {
  if (!iso) return '—';
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return `${Math.max(0, secs)}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

// Forecast remaining time from the backend's etaMs. null = un-forecastable
// (no recent progress); 0 = complete.
export function formatEta(ms: number | null): string {
  if (ms === null) return '—';
  if (ms <= 0) return 'done';
  const mins = Math.round(ms / 60_000);
  if (mins < 60) return `~${mins}m`;
  const hrs = Math.round(ms / 3_600_000);
  if (hrs < 48) return `~${hrs}h`;
  return `~${Math.round(ms / 86_400_000)}d`;
}
